import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import {
  ResponsiveAppShell,
  TaskyPageHeader,
  TaskySegmentedControl,
} from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import TaapBreakdownTable from '../../components/taap/TaapBreakdownTable';
import TaapKpiCard from '../../components/taap/TaapKpiCard';
import TaapProposalCard from '../../components/taap/TaapProposalCard';
import TaapScopePicker from '../../components/taap/TaapScopePicker';
import TaapSyncBar, { isInitialSyncRunning } from '../../components/taap/TaapSyncBar';
import TaapTabs, { adOperationsPath } from '../../components/taap/TaapTabs';
import TaapProductEmptyState from '../../components/taap/TaapProductEmptyState';
import { useTaapProductScope } from '../../components/taap/useTaapProductScope';
import taapService, { toScopeParams } from '../../services/api/taapService';
import {
  TaapBreakdown,
  TaapGranularity,
  TaapMetric,
  TaapPeriod,
  TaapPlatformFilter,
  TaapProposal,
  TaapSummary,
  TaapSyncStatus,
  TaapTimeseries,
} from '../../types/taap';
import { formatMoney, formatNumber, formatPercent, PLATFORM_LABELS } from '../../utils/taapFormat';

const PERIODS: TaapPeriod[] = [7, 14, 30, 90];
/** 初回取り込みは実測で数秒〜1分。5秒おきなら終わってから待たせない。 */
const INITIAL_SYNC_POLL_MS = 5000;
const PLATFORM_FILTERS: TaapPlatformFilter[] = ['all', 'meta', 'google'];

// SegmentedControl は value を文字列で持つので、期間だけ数値と往復させる。
const PERIOD_OPTIONS = PERIODS.map((value) => ({ value: String(value), label: `${value}日` }));

const PLATFORM_OPTIONS = PLATFORM_FILTERS.map((value) => ({
  value,
  label: value === 'all' ? '全体' : PLATFORM_LABELS[value] ?? value,
}));

const NOT_FOUND_NOTICE = '商品・アカウントが見つからないため、先頭の商品に戻しました。';

const METRIC_OPTIONS: { key: TaapMetric; label: string }[] = [
  { key: 'cv', label: 'CV' },
  { key: 'cost', label: '費用' },
  { key: 'cpa', label: 'CPA' },
  { key: 'ctr', label: 'CTR' },
];

const METRIC_SEGMENTS = METRIC_OPTIONS.map((option) => ({ value: option.key, label: option.label }));

const GRANULARITIES: { key: TaapGranularity; label: string }[] = [
  { key: 'daily', label: '日次' },
  { key: 'weekly', label: '週次' },
  { key: 'monthly', label: '月次' },
];

const GRANULARITY_SEGMENTS = GRANULARITIES.map((option) => ({
  value: option.key,
  label: option.label,
}));

/**
 * TAAP 広告運用ダッシュボード。
 *
 * 表示する数値はすべて同期済みの daily_kpis から来る。媒体 API を直接
 * 叩かないので、媒体側が落ちていても最後に取り込んだ実績を見せられる。
 */
const TaapDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { search } = useLocation();

  // 選択中の商品・アカウントは URL を正本にする。state と二重管理しないで済むうえ、
  // 「この商品だけ」の画面がそのまま共有・リロードできる。
  const scope = useTaapProductScope();
  const { productId, accountId, product, productAccounts, recoverToFirstProduct } = scope;
  const ready = productId !== null;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [period, setPeriod] = useState<TaapPeriod>(30);
  const [platform, setPlatform] = useState<TaapPlatformFilter>('all');
  const [metric, setMetric] = useState<TaapMetric>('cv');
  const [granularity, setGranularity] = useState<TaapGranularity>('daily');

  const [summary, setSummary] = useState<TaapSummary | null>(null);
  const [timeseries, setTimeseries] = useState<TaapTimeseries | null>(null);
  const [breakdown, setBreakdown] = useState<TaapBreakdown | null>(null);
  const [syncStatus, setSyncStatus] = useState<TaapSyncStatus | null>(null);
  const [proposals, setProposals] = useState<TaapProposal[]>([]);

  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const coreRequest = useRef(0);
  const chartRequest = useRef(0);

  // 商品・アカウントを変えたら媒体の絞り込みは持ち越さない。持ち越すと
  // 「Meta だけの商品 × Google」のような必ず空になる組み合わせを作れてしまう。
  const { selectProduct: writeProduct, selectAccount: writeAccount } = scope;
  const selectProduct = useCallback(
    (id: number) => {
      setNotice(null);
      setPlatform('all');
      writeProduct(id);
    },
    [writeProduct],
  );

  const selectAccount = useCallback(
    (id: number | null) => {
      setNotice(null);
      setPlatform('all');
      writeAccount(id);
    },
    [writeAccount],
  );

  const loadCore = useCallback(async () => {
    if (!ready) return;
    const requestId = ++coreRequest.current;
    setLoading(true);
    setError(null);
    setSummary(null);
    setBreakdown(null);
    setProposals([]);
    const scopeParams = toScopeParams(productId, accountId);
    const params = { period, platform, ...scopeParams };
    try {
      const [summaryData, breakdownData, syncData, proposalData] = await Promise.all([
        taapService.getSummary(params),
        taapService.getBreakdown(params),
        taapService.getSyncStatus(),
        taapService.getProposals('pending', scopeParams),
      ]);
      if (requestId !== coreRequest.current) return;
      setSummary(summaryData);
      setBreakdown(breakdownData);
      setSyncStatus(syncData);
      setProposals(proposalData.proposals);
    } catch (e) {
      if (requestId !== coreRequest.current) return;
      console.error('TAAP dashboard load failed:', e);
      // 紐付けが外れた商品・その商品に無いアカウントの id が URL に残っているケース。
      // API は範囲外の数字を返さないよう 404 で拒むので、先頭の商品に戻す。
      if ((e as { response?: { status?: number } })?.response?.status === 404 && (await recoverToFirstProduct({ productId, accountId }))) {
        if (requestId !== coreRequest.current) return;
        setPlatform('all');
        setNotice(NOT_FOUND_NOTICE);
        return;
      }
      if (requestId !== coreRequest.current) return;
      setError('ダッシュボードの読み込みに失敗しました。時間をおいて再度お試しください。');
    } finally {
      if (requestId === coreRequest.current) setLoading(false);
    }
  }, [ready, period, platform, productId, accountId, recoverToFirstProduct]);

  const loadChart = useCallback(async () => {
    if (!ready) return;
    const requestId = ++chartRequest.current;
    setChartLoading(true);
    setTimeseries(null);
    try {
      const result = await taapService.getTimeseries({
          period,
          platform,
          metric,
          granularity,
          ...toScopeParams(productId, accountId),
        });
      if (requestId === chartRequest.current) setTimeseries(result);
    } catch (e) {
      console.error('TAAP timeseries load failed:', e);
    } finally {
      if (requestId === chartRequest.current) setChartLoading(false);
    }
  }, [ready, period, platform, productId, accountId, metric, granularity]);

  useEffect(() => {
    loadCore();
    return () => { coreRequest.current += 1; };
  }, [loadCore]);

  useEffect(() => {
    loadChart();
    return () => { chartRequest.current += 1; };
  }, [loadChart]);

  // 接続直後の初回取り込みの最中は状態だけを取り直し、終わったら数値ごと読み直す。
  const initialSyncRunning = isInitialSyncRunning(syncStatus);
  useEffect(() => {
    if (!initialSyncRunning) return undefined;
    let cancelled = false;
    const timer = window.setInterval(() => {
      taapService
        .getSyncStatus()
        .then((next) => {
          if (cancelled) return;
          if (isInitialSyncRunning(next)) {
            setSyncStatus(next);
            return;
          }
          loadCore();
          loadChart();
        })
        .catch((e) => console.error('TAAP sync status poll failed:', e));
    }, INITIAL_SYNC_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [initialSyncRunning, loadCore, loadChart]);

  const currency = summary?.currency ?? 'JPY';
  const showRoas = Boolean(summary?.has_revenue);

  // アカウントが1つしかない商品にセレクタを出しても選ぶものが無い。
  const showAccountPicker = productAccounts.length > 1;
  const showAccountLevel = showAccountPicker && accountId === null;
  // 1媒体だけの商品・アカウントを選んだ状態では、媒体で絞っても数字が変わらない。
  const showPlatformFilter = accountId === null && (product?.platforms.length ?? 0) > 1;
  const chartData = useMemo(
    () => (timeseries?.points ?? []).map((point) => ({ ...point, label: point.label ?? point.date })),
    [timeseries],
  );

  const formatMetricValue = useCallback(
    (value: number) => {
      switch (metric) {
        case 'cost':
        case 'cpa':
          return formatMoney(value, currency);
        case 'ctr':
        case 'cvr':
          return formatPercent(value, 2);
        default:
          return formatNumber(value);
      }
    },
    [metric, currency],
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const sidebar = (
    <Sidebar onLogout={handleLogout} onToggleCollapse={setSidebarCollapsed} collapsed={sidebarCollapsed} />
  );

  const targetNote =
    summary?.target_cpa && summary.kpis.cpa.target_gap_rate !== undefined
      ? `目標 ${formatMoney(summary.target_cpa, currency)} 比 ${summary.kpis.cpa.target_gap_rate > 0 ? '+' : ''}${summary.kpis.cpa.target_gap_rate.toFixed(0)}%`
      : summary?.target_cpa
        ? `目標 ${formatMoney(summary.target_cpa, currency)}`
        : undefined;

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={<Sidebar onLogout={handleLogout} />}
      topbarTitle="広告運用"
    >
      <div className="flex min-h-[calc(100dvh_-_56px)] bg-warm-bg lg:h-screen lg:min-h-0">
        <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <TaskyPageHeader
            data-testid="ad-operations-header"
            title="広告運用ダッシュボード"
            description={
              <>
                <Link to="/account/settings#notification-integrations" className="text-primary underline">
                  レポート通知設定
                </Link>
              </>
            }
            actions={<span className="hidden items-center lg:flex"><TaskyHeaderActions /></span>}
            toolbar={
              ready ? (
              <>
                <TaapScopePicker scope={scope} onSelectProduct={selectProduct} onSelectAccount={selectAccount} />

                {showPlatformFilter && (
                  <TaskySegmentedControl<TaapPlatformFilter>
                    aria-label="媒体で絞り込む"
                    options={PLATFORM_OPTIONS}
                    value={platform}
                    onChange={setPlatform}
                  />
                )}

                <TaskySegmentedControl
                  aria-label="集計期間"
                  options={PERIOD_OPTIONS}
                  value={String(period)}
                  onChange={(value) => setPeriod(Number(value) as TaapPeriod)}
                />
              </>
              ) : undefined
            }
          />

          <div className="mx-auto w-full max-w-[1400px] p-6 max-[640px]:p-4">
            <div className="mb-4" data-tutorial="taap-tabs">
              <TaapTabs active="dashboard" pendingCount={proposals.length} />
            </div>

            {ready ? (
              <>
                <div className="mt-4">
                  <TaapSyncBar
                    status={syncStatus}
                    loading={loading}
                    onConnect={() => navigate('/settings/media-connect')}
                  />
                </div>

                {notice && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">
                    {notice}
                  </div>
                )}

                {error && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
                    {error}
                  </div>
                )}

                {/* KPI */}
                <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4" data-tutorial="taap-kpi">
                  <TaapKpiCard
                    metricKey="cost"
                    label="費用（広告費）"
                    metric={summary?.kpis.cost ?? null}
                    formatValue={(value) => formatMoney(value, currency)}
                  />
                  <TaapKpiCard
                    metricKey="conversions"
                    label="CV（獲得件数）"
                    metric={summary?.kpis.conversions ?? null}
                    formatValue={(value) => `${formatNumber(value)} 件`}
                  />
                  <TaapKpiCard
                    metricKey="cpa"
                    label="CPA（獲得単価）"
                    hint="低いほど良い指標です"
                    metric={summary?.kpis.cpa ?? null}
                    // CV が 0 の期間では CPA を計算できない。API は 0 を返すが
                    // 「獲得単価 ¥0」は無料で獲得できたように読めるため伏せる。
                    formatValue={(value) =>
                      (summary?.kpis.conversions.value ?? 0) > 0 ? formatMoney(value, currency) : '—'
                    }
                    note={targetNote}
                  />
                  {showRoas ? (
                    <TaapKpiCard
                      metricKey="roas"
                      label="ROAS（広告費の回収率）"
                      metric={summary?.kpis.roas ?? null}
                      formatValue={(value) => formatPercent(value, 0)}
                    />
                  ) : (
                    <TaapKpiCard
                      metricKey="cvr"
                      label="CVR（クリック後の獲得率）"
                      hint="売上が広告に紐づかない商材のため ROAS ではなく CVR を表示しています"
                      metric={summary?.kpis.cvr ?? null}
                      formatValue={(value) => formatPercent(value, 2)}
                    />
                  )}
                </div>

                {/* 推移 */}
                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4" data-tutorial="taap-chart">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-gray-900">
                      {METRIC_OPTIONS.find((option) => option.key === metric)?.label} の推移
                    </h2>
                    <div className="flex flex-wrap items-center gap-2">
                      <TaskySegmentedControl<TaapMetric>
                        aria-label="表示する指標"
                        size="sm"
                        options={METRIC_SEGMENTS}
                        value={metric}
                        onChange={setMetric}
                      />
                      <TaskySegmentedControl<TaapGranularity>
                        aria-label="集計の粒度"
                        size="sm"
                        options={GRANULARITY_SEGMENTS}
                        value={granularity}
                        onChange={setGranularity}
                      />
                    </div>
                  </div>

                  <div className="h-[260px]">
                    {chartLoading && chartData.length === 0 ? (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">読み込み中…</div>
                    ) : chartData.length === 0 ? (
                      <div className="flex h-full items-center justify-center text-sm text-gray-500">
                        表示できるデータがまだありません
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#EEE9DD" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6b7280' }}
                            tickLine={false}
                            axisLine={false}
                            width={70}
                            tickFormatter={(value: number | undefined) =>
                              value === undefined ? '' : formatMetricValue(value)
                            }
                          />
                          <RechartsTooltip
                            formatter={(value: number | undefined) =>
                              value === undefined ? '—' : formatMetricValue(value)
                            }
                          />
                          <Line type="monotone" dataKey="value" stroke="#616FC9" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* 今日やること */}
                <div className="mt-6" data-tutorial="taap-proposals">
                  <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">
                      今すぐ効く改善
                      {proposals.length > 0 && (
                        <span className="ml-2 text-xs font-normal text-gray-500">{proposals.length}件</span>
                      )}
                    </h2>
                    <button
                      type="button"
                      onClick={() => navigate(adOperationsPath('/taap/proposals', search))}
                      className="text-xs text-primary hover:underline"
                    >
                      提案一覧を開く
                    </button>
                  </div>

                  {proposals.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 bg-white px-5 py-6 text-center text-sm text-gray-500">
                      未承認の改善提案はありません
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {proposals.slice(0, 3).map((proposal) => (
                        <TaapProposalCard
                          key={proposal.id}
                          proposal={proposal}
                          currency={currency}
                          compact
                          onOpenDetail={() => navigate(adOperationsPath(`/taap/proposals/${proposal.id}`, search))}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* 媒体別 */}
                <div className="mt-6">
                  <h2 className="mb-2 text-sm font-semibold text-gray-900">
                    {showAccountLevel ? 'アカウント別パフォーマンス' : '媒体別パフォーマンス'}
                  </h2>
                  <TaapBreakdownTable
                    breakdown={breakdown}
                    loading={loading}
                    currency={currency}
                    showRoas={showRoas}
                    showAccountLevel={showAccountLevel}
                  />
                </div>

              </>
            ) : (
              <div className="mt-4">
                {scope.loadFailed ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
                    商品を読み込めませんでした。時間をおいて再度お試しください。
                  </div>
                ) : scope.loaded ? (
                  <TaapProductEmptyState />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default TaapDashboardPage;
